import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Plus, Star } from "lucide-react";
import { toast } from "sonner";

export const MyReviews = () => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api.get(`/reviews?influencer_id=${user.user_id}`).then(r => setReviews(r.data));
  }, [user.user_id]);
  return (
    <div data-testid="my-reviews">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-black">Review Saya</h1>
          <p className="text-muted-foreground">Posting & kelola review-mu</p>
        </div>
        <Link to="/dashboard/reviews/new"><Button className="rounded-full" data-testid="new-review-btn"><Plus className="w-4 h-4 mr-2" />Tulis Review</Button></Link>
      </div>
      {reviews.length === 0 ? (
        <div className="warm-card p-12 text-center">
          <Star className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Belum ada review.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reviews.map(rv => (
            <div key={rv.id} className="warm-card p-5" data-testid={`my-review-${rv.id}`}>
              <div className="text-xs text-muted-foreground">{rv.restaurant?.name}</div>
              <h3 className="font-bold mt-1">{rv.title}</h3>
              <div className="flex gap-3 mt-3 text-xs">
                <span>Rasa <strong>{rv.rasa}</strong></span>
                <span>Harga <strong>{rv.harga}</strong></span>
                <span>Amb <strong>{rv.ambience}</strong></span>
                <span>Score <strong>{rv.overall}</strong></span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ReviewForm = () => {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [form, setForm] = useState({
    restaurant_id: "", title: "", content: "", rasa: 8, harga: 8, ambience: 8, overall: 8,
    instagram_video_url: "", cover_image: ""
  });

  useEffect(() => {
    api.get("/restaurants?limit=100").then(r => setRestaurants(r.data));
  }, []);

  const onCoverUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/upload", fd);
      setForm({ ...form, cover_image: `${process.env.REACT_APP_BACKEND_URL}${r.data.url}` });
      toast.success("Foto diupload");
    } catch { toast.error("Upload gagal"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", form);
      toast.success("Review terposting!");
      navigate("/dashboard/reviews");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal posting");
    }
  };

  return (
    <div data-testid="review-form">
      <h1 className="font-heading text-3xl font-black mb-6">Tulis Review Influencer</h1>
      <form onSubmit={submit} className="warm-card p-6 space-y-4">
        <div>
          <Label>Restoran *</Label>
          <Select value={form.restaurant_id} onValueChange={v => setForm({ ...form, restaurant_id: v })}>
            <SelectTrigger data-testid="rev-restaurant"><SelectValue placeholder="Pilih restoran" /></SelectTrigger>
            <SelectContent>
              {restaurants.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Judul Review *</Label>
          <Input required data-testid="rev-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Contoh: BAKMI INI BIKIN NAGIH! 🔥" />
        </div>
        <div>
          <Label>Isi Review</Label>
          <Textarea data-testid="rev-content" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={4} />
        </div>

        {[
          { key: "rasa", label: "Rasa" },
          { key: "harga", label: "Harga" },
          { key: "ambience", label: "Ambience" },
          { key: "overall", label: "Overall Score" },
        ].map(({ key, label }) => (
          <div key={key}>
            <div className="flex justify-between mb-2">
              <Label>{label}</Label>
              <span className="font-heading font-black text-primary">{form[key]}</span>
            </div>
            <Slider
              data-testid={`rev-${key}`}
              min={0} max={10} step={0.1}
              value={[form[key]]}
              onValueChange={v => setForm({ ...form, [key]: v[0] })}
            />
          </div>
        ))}

        <div>
          <Label>Link Instagram Video Review *</Label>
          <Input
            data-testid="rev-ig-url"
            required
            value={form.instagram_video_url}
            onChange={e => setForm({ ...form, instagram_video_url: e.target.value })}
            placeholder="https://www.instagram.com/reel/..."
          />
        </div>

        <div>
          <Label>Cover Image (opsional)</Label>
          {form.cover_image && <img src={form.cover_image} alt="" className="w-full max-w-xs h-32 object-cover rounded-lg mb-2" />}
          <Input type="file" accept="image/*" onChange={onCoverUpload} data-testid="rev-cover-upload" />
        </div>

        <Button type="submit" className="rounded-full" data-testid="submit-review-btn">Posting Review</Button>
      </form>
    </div>
  );
};
